import {
  alpha,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { WarningAmber as WarningAmberIcon } from "@mui/icons-material";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { Link as RouterLink } from "react-router-dom";
import { getCriticalExposuresUrl } from "~/lib/urlUtils";

import { FleetIcon } from "~/icons";
import { useCriticalExposuresCard } from "~/pages/space/home-dashboard/critical-exposures/hooks/useCriticalExposuresCard";

interface Props {
  scope?: SpaceOrWorkspaceScope;
}

export const CriticalExposuresCardV2 = ({ scope }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(600));
  const { isLoading, criticalExposures } = useCriticalExposuresCard(scope?.mrn);

  const exposuresCount =
    typeof criticalExposures?.exposures_count === "number"
      ? criticalExposures.exposures_count
      : 0;
  const assetsCount =
    typeof criticalExposures?.assets_count === "number"
      ? criticalExposures.assets_count
      : 0;

  const hasNoExposures = !isLoading && exposuresCount === 0;
  const hasExposures = !isLoading && exposuresCount > 0;

  const exposuresCardStyle = {
    border: `2px solid ${hasExposures ? theme.palette.critical?.main : hasNoExposures ? theme.palette.none.main : "0"}`,
    backgroundColor: `${hasExposures ? alpha(theme.palette.critical?.light, 0.09) : hasNoExposures ? alpha(theme.palette.none?.light, 0.09) : ""}`,
  };

  const cardUrl = getCriticalExposuresUrl(scope);

  return (
    <Grid container spacing={2}>
      <Paper
        data-testid="critical-exposures-card"
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "relative",
          flexDirection: isMobile ? "column" : "row",
          minWidth: 0,
          width: 1,
          cursor: "pointer",
          ...exposuresCardStyle,
        }}
        component={RouterLink}
        to={cardUrl}
        aria-label="View critical exposures details"
      >
        <Grid
          sx={{ display: "flex", flexDirection: "column", gap: "5px" }}
          size={{
            md: 8,
          }}
        >
          <Typography
            variant={isMobile ? "subtitle1" : "h5"}
            fontWeight={700}
            sx={{ display: "flex", alignItems: "center", gap: "4px" }}
          >
            <WarningAmberIcon />
            CRITICAL EXPOSURES
          </Typography>
          <Typography
            color="text.secondary"
            variant="caption"
            sx={{
              textTransform: "uppercase",
              fontSize: {
                sm: 10,
                md: 12,
              },
            }}
          >
            To avert a likely breach, fix these findings IMMEDIATELY
          </Typography>
        </Grid>

        <Grid
          sx={{
            display: "flex",
            alignItems: isMobile ? "center" : "flex-end",
            flexDirection: "column",
          }}
          size={{
            sm: 4,
          }}
        >
          <Typography
            variant={isMobile ? "h6" : "h5"}
            fontWeight={700}
            data-testid="critical-exposures-count"
          >
            {exposuresCount} Findings
          </Typography>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <FleetIcon sx={{ fontSize: 16 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="critical-exposures-assets-count"
            >
              on {assetsCount} Assets
            </Typography>
          </Stack>
        </Grid>
      </Paper>
    </Grid>
  );
};

CriticalExposuresCardV2.displayName = "CriticalExposuresCardV2";
